/** Transient gateway / rate-limit statuses worth retrying. */
export const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 502, 503, 504]);

export function isRetryableHttpStatus(status: number): boolean {
  return RETRYABLE_HTTP_STATUSES.has(status);
}

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

/** Network failures (not aborts) and selected HTTP statuses. */
export function shouldRetryRequest(opts: {
  status?: number;
  error?: unknown;
}): boolean {
  if (opts.error != null) {
    return !isAbortError(opts.error);
  }
  if (opts.status == null) return false;
  return isRetryableHttpStatus(opts.status);
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export interface RetryAttemptInfo {
  /** 1-based attempt that just failed (before the upcoming retry). */
  attempt: number;
  /** Total attempts configured. */
  retries: number;
  delayMs: number;
  status?: number;
  error?: unknown;
}

export interface FetchWithRetryOptions {
  /**
   * Total attempts including the first. Default 3 → delays 500ms, 1s between retries
   * when `baseDelayMs` is 500.
   */
  retries?: number;
  /** Delay before the second attempt; doubles each time. Default 500. */
  baseDelayMs?: number;
  /** Return true to retry this failure. Defaults to gateway/network rules. */
  retryOn?: (ctx: { status?: number; error?: unknown; attempt: number }) => boolean;
  onRetry?: (info: RetryAttemptInfo) => void;
}

/**
 * `fetch` with exponential backoff for transient failures.
 * Returns the last Response (including non-OK) when retries are exhausted for
 * HTTP errors; rethrows the last network error when the request never reached
 * the server.
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const retries = Math.max(1, options.retries ?? 3);
  const baseDelayMs = options.baseDelayMs ?? 500;
  const retryOn = options.retryOn ?? ((ctx) => shouldRetryRequest(ctx));
  const signal = init?.signal ?? undefined;

  let lastNetworkError: unknown;

  for (let attempt = 1; attempt <= retries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const response = await fetch(input, init);
      const retryable =
        attempt < retries && retryOn({ status: response.status, attempt });
      if (retryable) {
        try {
          await response.body?.cancel();
        } catch {
          // ignore cancel errors
        }
        const delayMs = baseDelayMs * 2 ** (attempt - 1);
        options.onRetry?.({
          attempt,
          retries,
          delayMs,
          status: response.status,
        });
        await sleep(delayMs, signal);
        continue;
      }
      return response;
    } catch (error) {
      if (isAbortError(error)) throw error;
      lastNetworkError = error;
      const retryable = attempt < retries && retryOn({ error, attempt });
      if (retryable) {
        const delayMs = baseDelayMs * 2 ** (attempt - 1);
        options.onRetry?.({
          attempt,
          retries,
          delayMs,
          error,
        });
        await sleep(delayMs, signal);
        continue;
      }
      throw error;
    }
  }

  throw lastNetworkError instanceof Error
    ? lastNetworkError
    : new Error('Request failed after retries');
}
